/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import { debounce } from 'lodash';
import Tooltip from '../info-elements/tooltip';

type AutoCompleteProps = Omit<
AutocompleteProps<
any,
boolean | undefined,
boolean | undefined,
boolean | undefined,
React.ElementType
>,
'renderInput'
>;

export type Option = {
  value: string;
  label: string;
  disabled?: true;
  tooltipText?: string;
};

export function normalizeOptionValue<T extends { value: string }>(option: T | null | undefined): T | null {
  return option?.value == null ? null : option;
}

type MenuItemProps = {
  option: Option;
  params: React.HTMLAttributes<HTMLLIElement>;
};

type ParamsWithKey = React.HTMLAttributes<HTMLLIElement> & { key: React.Key };

interface Props extends AutoCompleteProps {
  readonly label?: string;
  readonly isError?: boolean; // error fetching the resource
  readonly errorResource: string;
  readonly options: Option[];
  readonly onSetFilter?: (text: string) => void;
  readonly hidden?: boolean;
  readonly formBuilderMode?: boolean;
  readonly hasValidationError?: boolean; // error with input
  readonly helperText?: string;
  readonly placeholder?: string;
}

const errorFetchingText = (resource: string) => (
  <Typography color="error.main">
    Error fetching
    {' '}
    {resource}
    {' '}
    .
  </Typography>
);

const MenuItem = ({ option, params }: MenuItemProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { key, ...restParams } = params as ParamsWithKey;
  return (
    <li data-testid={`menu-item-${option.value}`} key={option.value} {...restParams}>
      <Box
        title={option.label}
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {option.label}
      </Box>
    </li>
  );
};

const MenuItemDisabled = ({ option, params }: MenuItemProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { key, ...restParams } = params as ParamsWithKey;
  return (
    <Tooltip
      key={option.value}
      overflow={false}
      placement="left"
      title={option.tooltipText ?? ''}
    >
      <li {...restParams} data-testid={`menu-item-disabled-${option.value}`}>
        <div style={{ display: 'flex' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <InfoOutlinedIcon fontSize="small" color="disabled" sx={{ mr: 1 }} />
            <Box
              title={option.label}
              sx={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {option.label}
            </Box>
          </div>
        </div>
      </li>
    </Tooltip>
  );
};

// ignores spaces when matching
const filterOptions = createFilterOptions<Option>({
  stringify: (option: Option) => option.label.replace(/\s/g, ''),
});

export default function SelectTypeAhead({
  errorResource,
  isError,
  onSetFilter,
  formBuilderMode,
  hasValidationError,
  helperText,
  placeholder,
  label,
  ...props
}: Props) {
  const [filter, setFilter] = useState({ text: '', reason: '' });
  const [open, setOpen] = useState(false);

  // the form builder mode uses strict 'input' check
  const isValidFilter = formBuilderMode
    ? filter.reason === 'input'
    : filter.reason.length && filter.reason !== 'reset';

  const handleInputChange = useMemo(
    () => debounce((text) => {
      if (onSetFilter) {
        onSetFilter(text || '');
      }
    }, 200),
    [onSetFilter],
  );

  useEffect(() => () => {
    handleInputChange.cancel();
  }, [handleInputChange]);

  useEffect(() => {
    if (isValidFilter && (!filter.text || filter.text.length >= 2)) {
      handleInputChange(filter.text);
    }
  }, [filter, isValidFilter, handleInputChange]);

  const handleInputChangeWithSpaceDetection = (
    evt: React.SyntheticEvent,
    text: string,
    reason: string,
  ) => {
    // for a11y, if the input is just a space (or only spaces) and dropdown is closed, open it and clear the filter
    if (text.trim() === '' && text.length > 0 && !open) {
      setOpen(true);
      setFilter({ text: '', reason: 'clear' });
    } else {
      setFilter({ text, reason });
    }
  };

  return (
    <>
      {!!isError && errorFetchingText(errorResource)}
      {!props.hidden && !isError && (
        <Autocomplete
          {...props}
          fullWidth
          disablePortal
          disableClearable
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          filterOptions={filterOptions}
          loadingText={props.loadingText || 'Loading Items...'}
          options={props.options ?? []}
          value={normalizeOptionValue(props.value)}
          inputValue={filter.text || ''}
          onInputChange={handleInputChangeWithSpaceDetection}
          renderOption={(params, option: Option) => (
            <div key={option.value}>
              {!option.disabled ? (
                <MenuItem params={params} option={option} />
              ) : (
                <MenuItemDisabled params={params} option={option} />
              )}
            </div>
          )}
          getOptionDisabled={(option: Option) => option.disabled ?? false}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={placeholder}
              error={hasValidationError ?? false}
              helperText={helperText ?? ''}
              {...(formBuilderMode ? {
                variant: 'standard',
              } : {})}
              InputProps={{
                ...params.InputProps,
                ...(formBuilderMode ? {
                  disableUnderline: true,
                } : {}),
                endAdornment: (
                  <>
                    {props.loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              InputLabelProps={{
                ...params.InputLabelProps,
                ...(formBuilderMode && !label && {
                  shrink: false,
                  sx: { display: 'none' },
                }),
              }}
              {...(formBuilderMode ? {
                sx: {
                  '& .MuiInput-root .MuiInput-input': {
                    padding: '10px !important',
                  },
                  '& .MuiInputBase-input.MuiInput-input, & .MuiInputBase-input.MuiInput-input:hover, & .MuiInputBase-input.MuiInput-input:focus': {
                    transition: 'none !important',
                  },
                  '& .MuiInputBase-input.MuiInput-input:hover': {
                    border: '1px solid !important',
                    borderColor: 'primary.main !important',
                  },
                  '& .MuiInputBase-input.MuiInput-input:focus': {
                    border: '2px solid !important',
                    borderColor: 'primary.main !important',
                    outline: '0 !important',
                    margin: '-1px !important',
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    right: '8px',
                    backgroundColor: 'background.paper',
                  },
                  '& .MuiInputBase-input.MuiInput-input.Mui-disabled': {
                    backgroundColor: 'background.paper',
                    color: 'text.disabled',
                  },
                },
              } : {
                // Hide the notch when no label is provided (outlined variant only)
                ...(!label && {
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': { top: '-1px' },
                    '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
                  },
                }),
              })}
            />
          )}
        />
      )}
    </>
  );
}
